import { useState, useEffect } from "react";

// Module-level singleton that tracks file uploads outside of React.
// Uploads keep reporting here even after the page/component that started
// them has unmounted (closing the upload dialog, navigating to another route).
//
// Components read it via the useUploads() hook, which re-renders them
// whenever anything changes.
//
// Uploads are queued: at most MAX_CONCURRENT_UPLOADS transfer at once,
// the rest wait with status "queued" and start as slots free up. This
// stops ten dropped videos from fighting for uplink simultaneously.

const MAX_CONCURRENT_UPLOADS = 3;

let uploads = [];
const listeners = new Set();

function notify() {
    // New array reference each time so React state updates trigger re-renders
    uploads = [...uploads];
    listeners.forEach((listener) => listener(uploads));
}

function findUpload(key) {
    return uploads.find((u) => u.file.key === key);
}

function transferringCount() {
    return uploads.filter((u) => u.status === "uploading").length;
}

// Start queued uploads while there are free slots
function maybeStartNext() {
    while (transferringCount() < MAX_CONCURRENT_UPLOADS) {
        const next = uploads.find((u) => u.status === "queued");
        if (!next) break;

        // Set status before calling beginFn - it may report
        // progress/errors synchronously
        next.status = "uploading";

        try {
            next.beginFn();
        } catch (e) {
            next.status = "error";
            next.errorMessage = e.message || "Upload failed to start";
        }
    }

    notify();
}

const uploadManager = {
    getUploads() {
        return uploads;
    },

    // Returns an unsubscribe function
    subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },

    // Register a new upload. beginFn starts the actual transfer and is
    // called by the manager when a concurrency slot is free. It's also
    // reused to retry failed (non-resumable) uploads from scratch.
    start(fileMetadata, beginFn) {
        // Housekeeping: drop finished entries the user has already dismissed,
        // so they don't pollute the next batch's summary counts and the
        // array doesn't grow forever
        uploads = uploads.filter(
            (u) =>
                u.show !== false ||
                u.status === "uploading" ||
                u.status === "queued",
        );

        // Replace any existing entry for this file (e.g. a retry)
        uploads = uploads.filter((u) => u.file.key !== fileMetadata.key);

        uploads.push({
            file: fileMetadata,
            progress: 0,
            status: "queued", // queued | uploading | complete | error | cancelled
            show: true,
            beginFn: beginFn,
            tusUpload: null,
            storageTask: null,
            errorMessage: "",
        });

        notify();
        maybeStartNext();
    },

    progress(key, progress) {
        const upload = findUpload(key);
        if (!upload) return;

        // Skip the notify if nothing visible changed - progress events fire
        // far more often than the integer percentage moves
        if (upload.progress === progress && upload.status === "uploading") {
            return;
        }

        upload.progress = progress;
        upload.status = "uploading";
        notify();
    },

    // Store task references so uploads can be cancelled/resumed from anywhere
    setTask(key, { tusUpload = null, storageTask = null }) {
        const upload = findUpload(key);
        if (!upload) return;

        if (tusUpload) upload.tusUpload = tusUpload;
        if (storageTask) upload.storageTask = storageTask;
        notify();
    },

    complete(key) {
        const upload = findUpload(key);
        if (!upload) return;

        upload.progress = 100;
        upload.status = "complete";
        notify();
        maybeStartNext();
    },

    error(key, message) {
        const upload = findUpload(key);
        if (!upload) return;

        upload.status = "error";
        upload.errorMessage = message || "Upload failed";
        notify();
        maybeStartNext();
    },

    abort(key) {
        const upload = findUpload(key);
        if (!upload) return;

        // Queued uploads haven't started - just take them out of the queue
        if (upload.status !== "queued") {
            if (upload.tusUpload) {
                upload.tusUpload.abort();
            }

            if (upload.storageTask) {
                try {
                    upload.storageTask.cancel();
                } catch (e) {
                    // Task may already be complete/cancelled
                }
            }
        }

        upload.status = "cancelled";
        notify();
        maybeStartNext();
    },

    retry(key) {
        const upload = findUpload(key);
        if (!upload) return;

        if (upload.tusUpload) {
            // TUS uploads are resumable - continue from where it stopped
            // rather than re-running the whole upload
            upload.beginFn = () => upload.tusUpload.start();
        } else if (upload.beginFn) {
            // Non-resumable - the original beginFn re-runs from scratch
            upload.progress = 0;
        } else {
            return;
        }

        upload.status = "queued";
        upload.errorMessage = "";
        notify();
        maybeStartNext();
    },

    dismiss(key) {
        const upload = findUpload(key);
        if (!upload) return;

        upload.show = false;
        notify();
    },
};

// Warn before the tab/window closes while uploads are queued or in flight -
// closing at 92% of a 4GB video should not be a silent mistake
if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", (e) => {
        const hasActiveUploads = uploads.some(
            (u) => u.status === "uploading" || u.status === "queued",
        );

        if (hasActiveUploads) {
            e.preventDefault();
            // Required for Chrome to show the prompt
            e.returnValue = "";
        }
    });
}

// Hook for components that want to display upload state
export function useUploads() {
    const [current, setCurrent] = useState(uploadManager.getUploads());

    useEffect(() => {
        // Sync in case anything changed between initial render and subscribing
        setCurrent(uploadManager.getUploads());
        return uploadManager.subscribe(setCurrent);
    }, []);

    return current;
}

export default uploadManager;
